import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCashflow } from '../context/CashflowContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function NewCashflowPage() {
  const navigate = useNavigate();
  const { createCashflow, switchCashflow } = useCashflow();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    try {
      const newCashflow = await createCashflow({ name, description });
      switchCashflow(newCashflow);
      toast.success('Cashflow created!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating cashflow');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-2xl" data-testid="new-cashflow-page">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 text-stone-600 hover:text-stone-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl md:text-4xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          New Cashflow
        </h1>
        <p className="text-stone-500 mt-1">
          Create a new cashflow to track separate expenses
        </p>
      </div>

      {/* Form */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label>Cashflow Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Business Expenses"
                className="rounded-[4px]"
                data-testid="cashflow-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                className="rounded-[4px]"
                data-testid="cashflow-description-input"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="rounded-[4px]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
                data-testid="cashflow-submit-btn"
              >
                {saving ? 'Creating...' : 'Create Cashflow'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
